import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Bars3Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

const productLinks = [
  { name: 'Features', href: '/features', description: 'What the product actually does' },
  { name: 'Product tour', href: '/product-tour', description: 'Screens from the demo organization' },
  { name: 'In the product', href: '/roadmap', description: 'Shipped, and what is not built' },
];

const links = [
  { name: 'Use cases', href: '/use-cases' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

function itemClass(active: boolean) {
  return `inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    active ? 'bg-primary-50 text-forest' : 'text-ink-soft hover:bg-paper-deep hover:text-ink'
  }`;
}

export const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setProductOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onPointer = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setProductOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, []);

  const productActive = productLinks.some((item) => location.pathname === item.href);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-paper/95 backdrop-blur-md">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <nav className="container-width section-padding" ref={menuRef} aria-label="Primary">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex min-h-11 items-center gap-2.5">
            <img src="/images/SynqTag.svg" alt="" className="h-8 w-8" />
            <span className="font-display text-xl text-ink">SynqTag</span>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            <div className="relative">
              <button
                type="button"
                className={itemClass(productActive)}
                aria-expanded={productOpen}
                aria-controls="product-menu"
                onClick={() => setProductOpen((value) => !value)}
              >
                Product
                <ChevronDownIcon className={`ml-1 inline h-4 w-4 ${productOpen ? 'rotate-180' : ''}`} />
              </button>
              {productOpen && (
                <div
                  id="product-menu"
                  className="absolute left-0 top-full z-20 mt-2 w-72 rounded-xl border border-line bg-white p-2 shadow-lg"
                >
                  {productLinks.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block rounded-lg px-3 py-2.5 hover:bg-paper"
                    >
                      <span className="block text-sm font-semibold text-ink">{item.name}</span>
                      <span className="block text-sm text-ink-soft">{item.description}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {links.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => itemClass(isActive)}
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <a href="https://app.synqtag.com/auth/login" className="min-h-11 px-3 py-2 text-sm font-semibold text-ink-soft hover:text-ink">
              Sign in
            </a>
            <a href="https://app.synqtag.com/auth/register" className="btn-premium">
              Start free
            </a>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((value) => !value)}
          >
            <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
            {open ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div id="mobile-nav" className="border-t border-line py-3 lg:hidden">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-ink-soft">Product</p>
            {productLinks.map((item) => (
              <Link key={item.href} to={item.href} className="block rounded-lg px-3 py-3 text-base font-medium text-ink">
                {item.name}
              </Link>
            ))}
            {links.map((item) => (
              <Link key={item.href} to={item.href} className="block rounded-lg px-3 py-3 text-base font-medium text-ink">
                {item.name}
              </Link>
            ))}
            <div className="mt-2 grid gap-2 px-3 pb-2">
              <a href="https://app.synqtag.com/auth/login" className="btn-glass">
                Sign in
              </a>
              <a href="https://app.synqtag.com/auth/register" className="btn-premium">
                Start free
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
